import React, { useEffect, useState } from 'react';

import { Typography } from '@mui/material';

import AdditionModal from '../../../components/AdditionModal';
import Button from '../../../components/Button';
import CommitForm from '../../../components/CommitForm';
import ContentWrapper from '../../../components/ContentWrapper';
import PermissionsList from '../../../components/PermissionsList';
import {
  useGetPermissionsQuery,
  useSharePermissionMutation,
  useUnsharePermissionMutation,
  useUpdatePermissionGroupMutation,
} from '../../../services/permissions';
import { Permission, PermissionGroup } from '../../../services/permissions/types';
import { GroupPermissionType } from '../../../types/common';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import {
  HighlightText,
} from '../index.styles';

interface DetailsContentProps {
  group: PermissionGroup | undefined,
  groupId: string | undefined,
}

const DetailsContent = ({ group, groupId }: DetailsContentProps) => {
  const [targetPermissionId, setTargetPermissionId] = useState<string | undefined>(undefined);
  const [addPermissionModalOpen, setAddPermissionModalOpen] = useState(false);
  const [
    addPermissionModalType, setAddPermissionModalType,
  ] = useState<GroupPermissionType>(GroupPermissionType.Base);
  const [addPermissionModalSearchStr, setAddPermissionModalSearchStr] = useState('');

  const [sharePermission, sharePermissionOpts] = useSharePermissionMutation();
  const [unsharePermission, unsharePermissionOpts] = useUnsharePermissionMutation();
  const [updatePermissionGroup, updatePermissionGroupOpts] = useUpdatePermissionGroupMutation();
  const { data: permissionsList } = useGetPermissionsQuery({});

  const [basePermissions, setBasePermissions] = useState<Permission[]>([]);
  const [currPermissions, setCurrPermissions] = useState<Permission[]>([]);

  useEffect(() => {
    if (group?.basePermissions) {
      setBasePermissions(permissionsList?.data.filter((p) =>
        group.basePermissions.includes(p.value)) || []);
    }

    if (group?.currPermissions) {
      setCurrPermissions(permissionsList?.data.filter((p) =>
        group.currPermissions.includes(p.value)) || []);
    }
  }, [permissionsList, group]);

  useEffect(() => {
    // When the add modal is open or closed, reset errors
    setTimeout(
      // Wait for close animation to finish
      () => sharePermissionOpts.reset(),
      200,
    );
  }, [addPermissionModalOpen]);

  useSuccess(unsharePermissionOpts.isSuccess, 'Permission removed');
  useError(unsharePermissionOpts.isError, unsharePermissionOpts.error);
  useError(updatePermissionGroupOpts.isError, updatePermissionGroupOpts.error);

  return (
    <>
      <ContentWrapper title="Group Details">
        <CommitForm
          data={{
            groupId, label: group?.name,
          }}
          metadata={{
            groupId:
            {
              label: 'Group ID',
              editable: false,
              type: 'text',
            },
            label: {
              label: 'Group Name',
              editable: true,
              type: 'text',
            },
          }}
          fields={{
            groupId: {
              xs: 12,
              md: 12,
            },
            label: {
              xs: 12,
              md: 12,
            },
          }}
          onSubmit={async (values) => updatePermissionGroup({
            group: groupId || '',
            attributes: values,
          })}
          isSubmitLoading={updatePermissionGroupOpts.isLoading}
          autoSubmit
        />
      </ContentWrapper>
      <ContentWrapper
        title="Base Permissions"
        caption={(
          <Typography variant="caption">
            <HighlightText>Base Permissions</HighlightText> are applied to all
            existing and new members of the group.
          </Typography>
        )}
      >
        <PermissionsList
          data={basePermissions}
          columns={[{
            field: 'label',
            headerName: 'Label',
            width: 400,
          },
          {
            field: 'control',
            headerName: 'Control',
            width: 100,
            renderCell: ({ row }: any) => (
              <Button
                size="small"
                color="error"
                variant="text"
                allCaps
                disabled={unsharePermissionOpts.isLoading}
                loading={unsharePermissionOpts.isLoading
                              && targetPermissionId === row.id}
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setTargetPermissionId(row.id);
                  await unsharePermission({
                    groupId: groupId || '',
                    type: GroupPermissionType.Base,
                    tag: row.id,
                  });
                }}
              >
                Unshare
              </Button>
            ),
          }]}
          addButtonProps={{
            disabled: sharePermissionOpts.isLoading,
          }}
          onAddClick={() => {
            setAddPermissionModalType(GroupPermissionType.Base);
            setAddPermissionModalOpen(true);
          }}
          pageSize={5}
          getRowHref={(row: Permission) => `/admin/permission/${row.id}`}
        />
      </ContentWrapper>
      <ContentWrapper
        title="Current Permissions"
        caption={(
          <Typography variant="caption">
            <HighlightText>Current Permissions</HighlightText> are only applied to
            existing members of the group.
          </Typography>
        )}
      >
        <PermissionsList
          data={currPermissions}
          columns={[{
            field: 'label',
            headerName: 'Label',
            width: 400,
          },
          {
            field: 'control',
            headerName: 'Control',
            width: 100,
            renderCell: ({ row }: any) => (
              <Button
                size="small"
                color="error"
                variant="text"
                allCaps
                disabled={unsharePermissionOpts.isLoading}
                loading={unsharePermissionOpts.isLoading
                              && targetPermissionId === row.id}
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setTargetPermissionId(row.id);
                  await unsharePermission({
                    groupId: groupId || '',
                    type: GroupPermissionType.Current,
                    tag: row.id,
                  });
                }}
              >
                Unshare
              </Button>
            ),
          }]}
          addButtonProps={{
            disabled: sharePermissionOpts.isLoading,
          }}
          onAddClick={() => {
            setAddPermissionModalType(GroupPermissionType.Current);
            setAddPermissionModalOpen(true);
          }}
          pageSize={5}
          getRowHref={(row: Permission) => `/admin/permission/${row.id}`}
        />
      </ContentWrapper>
      <AdditionModal
        title="Add Permission"
        searchPlaceholder="Permission"
        open={addPermissionModalOpen}
        handleClose={() => {
          setAddPermissionModalOpen(false);
        }}
        onAdd={async (x: Permission) => sharePermission({
          groupId: groupId || '',
          type: addPermissionModalType,
          tag: x.value || '',
        })}
        isLoading={sharePermissionOpts.isLoading}
        isSuccess={sharePermissionOpts.isSuccess}
        isError={sharePermissionOpts.isError}
        error={(sharePermissionOpts.error as any)?.data?.message}
        closeNotifs={sharePermissionOpts.reset}
        searchFieldProps={{
          options: (permissionsList?.data || []).filter((p) => {
            // Exclude existing curr permission
            // If adding curr permissions, exclude existing base permissions too
            const isBasePermission = basePermissions.some(
              (x) => x.value === p.value,
            );
            if (addPermissionModalType === GroupPermissionType.Current && !isBasePermission) {
              // If adding curr permissions, exclude existing base permissions too
              const isCurrPermission = currPermissions.some(
                (x) => x.value === p.value,
              );
              return !isCurrPermission;
            }
            return !isBasePermission;
          }).filter(
            (p) => p.label.toLowerCase().includes(addPermissionModalSearchStr.toLowerCase()),
          ),
          getOptionLabel: (option: Permission) => option.label,
          getOptionKey: (option: Permission) => option.value,
          onSearch: (s) => setAddPermissionModalSearchStr(s),
        }}
      />
    </>
  );
};

export default DetailsContent;
